// Logic danh sách Job, chép từ script board Mint-Job (GRP · tab · lọc · sắp · cut-off < 48h).
import { fold } from '@hana/data';
import type { Job, JobGroup, ShipmentStatus } from './types';

const GROUP_OF: Record<ShipmentStatus, JobGroup> = {
  'RQ Request': 'Standby',
  'RQ Standby': 'Standby',
  'BK Standby': 'Standby',
  'BK Confirmed': 'Active',
  'BL Hold': 'Active',
  'BL Wait RLS': 'Active',
  'LS Job': 'Active',
  'BL Released': 'Released',
  'No Show': 'Cancelled',
  Cancelled: 'Cancelled',
  'RQ Cancel': 'Cancelled',
};

/** Nhóm trạng thái: job đã khoá kỳ (`close` = Locked) thành Invoiced, còn lại theo ShipmentStatus. */
export function jobGroup(status: ShipmentStatus, close: string | null): JobGroup {
  return close === 'Locked' ? 'Invoiced' : (GROUP_OF[status] ?? 'Active');
}

/** Mốc cuối của chuỗi tiến độ (CFM → ROD → FRT → LOA → SNO → FBL → RLS → DBN): số bước đã qua, 0–8. */
const PROGRESS: Partial<Record<ShipmentStatus, number>> = {
  'BK Standby': 1,
  'BK Confirmed': 2,
  'LS Job': 2,
  'BL Hold': 5,
  'BL Wait RLS': 6,
  'BL Released': 7,
};
export const PROGRESS_STEPS = 8;
export function jobProgress(job: Pick<Job, 'status' | 'close'>): number {
  return job.close === 'Locked' ? PROGRESS_STEPS : (PROGRESS[job.status] ?? 0);
}

/** Số giờ còn tới Closing CY (âm = đã qua); `null` khi chưa có mốc. */
export function cutoffHoursLeft(job: Pick<Job, 'cyCutoff'>, now: string): number | null {
  if (!job.cyCutoff) return null;
  return (Date.parse(job.cyCutoff) - Date.parse(now)) / 3_600_000;
}

/** Cut-off trong 48 giờ tới của job còn chạy (chip "sắp cut-off"). */
export function nearCutoff(job: Pick<Job, 'cyCutoff' | 'status' | 'close'>, now: string): boolean {
  const h = cutoffHoursLeft(job, now);
  return h !== null && h >= 0 && h < 48 && jobGroup(job.status, job.close) === 'Active';
}

export type JobTab = 'all' | 'mine' | 'run' | 'sby' | 'frt' | 'ved' | 'eta' | 'wclose' | JobGroup;
/** Người đang dùng (board: DAVID) cho tab "Của tôi"; đổi sang người đăng nhập khi có phiên thật. */
export const JOB_ME = 'DAVID';
/** Tab chính trên đầu trang (Mint-Job) và tab trong menu "Thêm". */
export const JOB_TABS: ReadonlyArray<{ key: JobTab; label: string }> = [
  { key: 'all', label: 'Tất cả' },
  { key: 'mine', label: 'Của tôi' },
  { key: 'run', label: 'Đang chạy' },
  { key: 'sby', label: 'RQ / BK Standby' },
];
export const JOB_TABS_MORE: ReadonlyArray<{ key: JobTab; label: string }> = [
  { key: 'frt', label: 'FRT' },
  { key: 'wclose', label: 'Chờ đóng' },
  { key: 'ved', label: 'VED' },
  { key: 'eta', label: 'ETA Pending' },
  { key: 'Released', label: 'Released' },
  { key: 'Invoiced', label: 'Invoiced' },
  { key: 'Cancelled', label: 'Huỷ' },
];

const CONFIRMED: readonly ShipmentStatus[] = ['BK Confirmed', 'BL Hold', 'BL Wait RLS'];

/** FRT: đã BKCF, chưa có ASOrder (chưa khoá cước & dịch vụ). Dữ liệu mẫu: cờ `frt`. */
export function isFrtPending(j: Job): boolean {
  return j.bkcf !== undefined || j.asOrder !== undefined ? j.bkcf === true && j.asOrder === false : j.flags.includes('frt');
}
/** VED: cần khai và còn Pending. Dữ liệu mẫu: cờ `ved`. */
export function isVedPending(j: Job): boolean {
  return j.needVed !== undefined ? j.needVed && j.vedStatus === 'Pending' : j.flags.includes('ved');
}
/** Chờ đóng: đã debit, B/L Released, chưa đóng job. Chỉ hiển thị. */
export function isWaitClose(j: Job): boolean {
  const debited = j.debited ?? (j.close === 'Chờ đóng' || j.close === 'Quá hạn đóng');
  return debited && j.status === 'BL Released' && j.close !== 'Locked';
}
/** Số ngày từ gốc cố định tới ngày lịch `YYYY-MM-DD` (không dùng thứ trong tuần). */
const dayNo = (iso: string): number => Math.floor(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / 86_400_000);
/** Ngày lịch VN của một thời điểm ISO có múi giờ. */
const vnDay = (now: string): string => new Date(Date.parse(now) + 7 * 3_600_000).toISOString().slice(0, 10);
/** ETA Pending (QĐ #85): OB đã BKCF, chưa Released / hủy / no-show, ETA trong [hôm nay − 90, hôm nay + 2] ngày. */
export function isEtaPending(j: Job, now: string): boolean {
  if (j.dir !== 'OB' || !j.eta || j.bkcf === false) return false;
  if (j.bkcf === undefined && !CONFIRMED.includes(j.status)) return false;
  if (['BL Released', 'Cancelled', 'RQ Cancel', 'No Show'].includes(j.status)) return false;
  const d = dayNo(j.eta) - dayNo(vnDay(now));
  return d <= 2 && d >= -90;
}

function inTab(job: Job, tab: JobTab, now: string): boolean {
  const g = jobGroup(job.status, job.close);
  if (tab === 'all') return true;
  if (tab === 'mine') return job.sale === JOB_ME || job.cs === JOB_ME;
  if (tab === 'run') return g === 'Active';
  if (tab === 'sby') return g === 'Standby';
  if (tab === 'frt') return isFrtPending(job);
  if (tab === 'ved') return isVedPending(job);
  if (tab === 'eta') return isEtaPending(job, now);
  if (tab === 'wclose') return isWaitClose(job);
  return g === tab;
}

export type JobSortKey = 'id' | 'customer' | 'etd' | 'cut' | 'created' | 'pod' | 'progress' | 'shipment' | 'cargo';

export interface JobQuery {
  tab?: JobTab;
  /** tìm tự do: nhiều từ cách nhau bằng khoảng trắng, khớp bất kỳ từ nào (như board) */
  q?: string;
  mode?: Job['mode'][];
  dir?: Job['dir'][];
  cargo?: Job['cargo'][];
  sale?: string;
  /** "bây giờ" ISO có múi giờ, cho sắp theo cut-off */
  now: string;
  sort?: { key: JobSortKey; dir?: 'asc' | 'desc' };
}

const haystack = (j: Job): string =>
  fold(
    [j.id, j.customer, j.shipper, j.consignee, j.vessel, j.carrier, j.bookingNo, ...j.extraRefs, j.pol, j.pod, j.commodity, j.sale, j.cs, j.bkg]
      .filter(Boolean)
      .join(' '),
  );

/** Giá trị sắp; `null` luôn xuống cuối bất kể chiều. */
function sortValue(j: Job, key: JobSortKey, now: string): string | number | null {
  switch (key) {
    case 'id': return j.id;
    case 'customer': return j.customer;
    case 'etd': return j.etd;
    case 'cut': return cutoffHoursLeft(j, now);
    case 'created': return j.createdDay;
    case 'pod': return j.pod;
    case 'progress': return jobProgress(j);
    case 'shipment': return j.bookingNo ?? null;
    case 'cargo': return j.commodity || null;
  }
}

export function queryJobs(jobs: readonly Job[], query: JobQuery): Job[] {
  const tokens = (query.q ?? '').split(/\s+/).map(fold).filter(Boolean);
  let out = jobs.filter(
    (j) =>
      inTab(j, query.tab ?? 'all', query.now) &&
      (!query.mode?.length || query.mode.includes(j.mode)) &&
      (!query.dir?.length || query.dir.includes(j.dir)) &&
      (!query.cargo?.length || query.cargo.includes(j.cargo)) &&
      (!query.sale || j.sale === query.sale) &&
      (!tokens.length || tokens.some((t) => haystack(j).includes(t))),
  );
  if (query.sort) {
    const { key, dir = 'desc' } = query.sort;
    const k = dir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      const x = sortValue(a, key, query.now);
      const y = sortValue(b, key, query.now);
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * k;
      return String(x).localeCompare(String(y), 'vi') * k;
    });
  }
  return out;
}

/** Số job mỗi tab (chip đếm trên thanh tab), tính trên tập đã lọc ngoài tab. */
export function tabCounts(jobs: readonly Job[], now: string): Record<JobTab, number> {
  const out = {} as Record<JobTab, number>;
  for (const t of [...JOB_TABS, ...JOB_TABS_MORE]) out[t.key] = jobs.filter((j) => inTab(j, t.key, now)).length;
  return out;
}

/** Job "gấp" (chip nhanh trên iPad / điện thoại): sắp cut-off, quá hạn đóng hoặc có task quá hạn. */
export function isUrgent(job: Job, now: string): boolean {
  return nearCutoff(job, now) || job.close === 'Quá hạn đóng' || job.flags.includes('task');
}
